import type { ReactNode } from 'react'
import dayjs from 'dayjs'

import { Alert, Link } from '@atoms'
import Badge from '@components/atoms/Badge'
import { Progress } from '@components/atoms/Progress'
import { Card, CardHeading } from '@components/imports/Card'
import { ImportStatusBadge } from '@components/imports/ImportStatusBadge'
import cn from '@components/utils/cn'
import Icon from '@components/utils/Icon'
import {
  type Import,
  ImportFailureKind,
  ImportStatus,
  type ImportFailure,
  type ImportSummaryData,
  type ImportWarning,
  isSettled,
} from '@utils/imports/import'
import { scopedTranslation } from '@utils/I18n'

const t = scopedTranslation('imports.summary')
const tReason = scopedTranslation('imports.reasons')

export const FAILURES_ID = 'failures'

export const inlineLinkClasses =
  'tw:font-medium tw:text-primary-700 tw:border-b tw:border-transparent tw:hover:text-primary-900 tw:hover:border-current'

const rowsToReview = (item: Import) => (item.counts ? item.counts.failed + item.counts.skipped : 0)

export function ImportSummary({ import: item, canDownload }: ImportSummaryData) {
  const { failures, warnings, failedRowsHref } = item
  const settled = isSettled(item.status)

  return (
    <div className="tw:flex tw:flex-col tw:gap-5">
      {settled ? <SettledSummary import={item} /> : <WorkingSummary import={item} />}

      {failures.length > 0 && <FailuresTable failures={failures} settled={settled} failedRowsHref={failedRowsHref} />}

      {warnings.length > 0 && <WarningsTable warnings={warnings} />}

      {canDownload && item.fileHref && <FileCard import={item} />}
    </div>
  )
}

function CountTile({
  value,
  label,
  tone = 'default',
  muted,
}: {
  value: number
  label: string
  tone?: 'default' | 'warning' | 'error'
  muted?: boolean
}) {
  const tones = { default: 'tw:text-gray-cool-900', warning: 'tw:text-warning-700', error: 'tw:text-error-700' }

  return (
    <div className="tw:flex tw:flex-col tw:gap-0.5 tw:px-5 tw:py-4">
      <span className={cn('tw:text-2xl tw:font-bold tw:tabular-nums', muted ? 'tw:text-gray-cool-400' : tones[tone])}>
        {value.toLocaleString()}
      </span>
      <span className="tw:text-xs tw:text-gray-cool-600">{label}</span>
    </div>
  )
}

function ImportMeta({ import: item }: { import: Import }) {
  const values = {
    file: item.fileName ?? t('unnamed_file'),
    date: dayjs(item.submittedAt).format('D MMM YYYY, h:mm a'),
    name: item.importer,
  }

  return (
    <p className="tw:mb-0 tw:text-sm tw:text-gray-cool-600">
      {item.importer ? t('meta', values) : t('meta_without_name', values)}
    </p>
  )
}

function ImportHeader({
  import: item,
  heading,
  icon,
  iconTone,
}: {
  import: Import
  heading: string
  icon: ReactNode
  iconTone: string
}) {
  return (
    <div className="tw:flex tw:flex-wrap tw:items-start tw:gap-3 tw:border-b tw:border-gray-200 tw:px-5 tw:py-4">
      <span
        aria-hidden
        className={cn('tw:flex tw:h-9 tw:w-9 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full', iconTone)}
      >
        {icon}
      </span>
      <div className="tw:min-w-0 tw:flex-1">
        <h2 className="tw:m-0 tw:text-base tw:font-bold tw:text-gray-cool-900">{heading}</h2>
        <ImportMeta import={item} />
      </div>
      <ImportStatusBadge status={item.status} />
    </div>
  )
}

function ImportDetails({ import: item }: { import: Import }) {
  return (
    <dl className="tw:m-0 tw:flex tw:flex-wrap tw:gap-x-8 tw:gap-y-3 tw:border-t tw:border-gray-100 tw:px-5 tw:py-4">
      <div className="tw:flex tw:flex-col tw:gap-0.5">
        <dt className="tw:text-xs tw:font-normal tw:text-gray-cool-600">{t('details.group')}</dt>
        <dd className="tw:m-0 tw:text-sm tw:font-medium tw:text-gray-cool-900">{item.group.name}</dd>
      </div>
    </dl>
  )
}

function WorkingSummary({ import: item }: { import: Import }) {
  const queued = item.status === ImportStatus.Queued
  const stage = queued ? 'queued' : 'working'
  const total = item.submittedRowCount ?? 0
  const percent = total > 0 ? Math.round((item.processedRowCount / total) * 100) : 0
  const counts = item.counts ?? { created: 0, updated: 0, failed: 0, skipped: 0 }

  return (
    <Card>
      <ImportHeader
        import={item}
        heading={t(`working.${stage}_heading`)}
        iconTone="tw:bg-primary-50 tw:text-primary-600"
        icon={
          <Icon
            type="refresh"
            className={cn('tw:h-5 tw:w-5', !queued && 'tw:animate-spin tw:motion-reduce:animate-none')}
          />
        }
      />

      <div role="status" className="tw:flex tw:flex-col tw:gap-2 tw:px-5 tw:py-4">
        <div className="tw:flex tw:items-baseline tw:justify-between tw:gap-4 tw:text-sm">
          <span className="tw:font-medium tw:text-gray-cool-900">
            {queued
              ? t('working.rows_submitted', { count: total })
              : t('working.rows_processed', {
                  processed: item.processedRowCount.toLocaleString(),
                  total: total.toLocaleString(),
                })}
          </span>
          {!queued && <span className="tw:tabular-nums tw:text-gray-cool-600">{percent}%</span>}
        </div>
        <Progress
          value={queued ? 0 : percent}
          progressColor="tw:bg-primary-600"
          className="tw:bg-gray-cool-100"
          aria-label={t('working.progress_label')}
        />
        <p className="tw:mb-0 tw:text-xs tw:text-gray-cool-600">{t(`working.${stage}_hint`)}</p>
      </div>

      <div className="tw:grid tw:grid-cols-2 tw:divide-x tw:divide-gray-100 tw:border-t tw:border-gray-100 tw:sm:grid-cols-4">
        <CountTile value={counts.created} label={t('working.created')} muted={queued} />
        <CountTile value={counts.updated} label={t('working.updated')} muted={queued} />
        <CountTile value={counts.failed} label={t('working.failed')} tone="error" muted={queued} />
        <CountTile value={counts.skipped} label={t('working.skipped')} tone="warning" muted={queued} />
      </div>

      <ImportDetails import={item} />
    </Card>
  )
}

function SettledSummary({ import: item }: { import: Import }) {
  const toReview = rowsToReview(item)
  const failedImport = item.status === ImportStatus.Failed
  const counts = item.counts ?? { created: 0, updated: 0, failed: 0, skipped: 0 }

  const heading = failedImport
    ? t('settled.failed_heading')
    : toReview === 0
      ? t('settled.heading')
      : t('settled.heading_to_review', { count: toReview })

  const iconTone = failedImport
    ? 'tw:bg-error-50 tw:text-error-600'
    : toReview === 0
      ? 'tw:bg-success-50 tw:text-success-600'
      : 'tw:bg-warning-50 tw:text-warning-600'

  return (
    <Card>
      <ImportHeader
        import={item}
        heading={heading}
        iconTone={iconTone}
        icon={
          <Icon
            type={failedImport ? 'x-circle' : toReview === 0 ? 'check-circle' : 'alert-triangle'}
            className="tw:h-5 tw:w-5"
          />
        }
      />

      {item.counts && (
        <>
          <div className="tw:grid tw:grid-cols-2 tw:divide-x tw:divide-gray-100 tw:sm:grid-cols-4">
            <CountTile value={counts.created} label={t('settled.supporters_created')} />
            <CountTile value={counts.updated} label={t('settled.supporters_updated')} />
            <CountTile value={counts.failed} label={t('settled.rows_failed')} tone="error" />
            <CountTile value={counts.skipped} label={t('settled.rows_skipped')} tone="warning" />
          </div>

          {item.submittedRowCount !== null && (
            <p className="tw:mb-0 tw:border-t tw:border-gray-100 tw:px-5 tw:py-3 tw:text-xs tw:tabular-nums tw:text-gray-cool-600">
              {t('settled.reconciliation', {
                created: counts.created.toLocaleString(),
                updated: counts.updated.toLocaleString(),
                failed: counts.failed.toLocaleString(),
                skipped: counts.skipped.toLocaleString(),
              })}{' '}
              <span className="tw:font-medium tw:text-gray-cool-900">
                {t('settled.reconciliation_total', {
                  processed: item.processedRowCount.toLocaleString(),
                  total: item.submittedRowCount.toLocaleString(),
                })}
              </span>
            </p>
          )}
        </>
      )}

      {failedImport && (
        <div className="tw:border-t tw:border-gray-100 tw:px-5 tw:py-4">
          <Alert type="error" title={t('settled.failed_heading')}>
            <span className="tw:text-sm">{t('settled.failed_body')}</span>
          </Alert>
        </div>
      )}

      <ImportDetails import={item} />
    </Card>
  )
}

function FailuresTable({
  failures,
  settled,
  failedRowsHref,
}: {
  failures: ImportFailure[]
  settled: boolean
  failedRowsHref: string | null
}) {
  return (
    <Card id={FAILURES_ID}>
      <CardHeading
        icon="alert-triangle"
        title={settled ? t('failures.title', { count: failures.length }) : t('failures.title_working')}
        subtitle={t('failures.subtitle')}
        actions={
          failedRowsHref &&
          settled && (
            <Link href={failedRowsHref} rank="secondary" size="sm" leadingIcon="file-download-03">
              {t('failures.download')}
            </Link>
          )
        }
      />

      {!settled && (
        <p className="tw:mb-0 tw:border-b tw:border-gray-100 tw:px-5 tw:py-2 tw:text-xs tw:text-gray-cool-600">
          {t('failures.working_note')}
        </p>
      )}

      <div className="tw:max-h-[32rem] tw:overflow-auto">
        <table className="tw:min-w-full tw:border-collapse tw:text-sm">
          <thead className="tw:sticky tw:top-0">
            <tr className="tw:bg-gray-cool-50 tw:text-left tw:text-xs tw:text-gray-cool-600">
              <th scope="col" className="tw:w-16 tw:border-b tw:border-gray-200 tw:px-5 tw:py-2 tw:font-medium">
                {t('failures.columns.row')}
              </th>
              <th scope="col" className="tw:border-b tw:border-gray-200 tw:px-3 tw:py-2 tw:font-medium">
                {t('failures.columns.identifier')}
              </th>
              <th scope="col" className="tw:border-b tw:border-gray-200 tw:px-3 tw:py-2 tw:font-medium">
                {t('failures.columns.outcome')}
              </th>
              <th scope="col" className="tw:border-b tw:border-gray-200 tw:px-3 tw:py-2 tw:font-medium">
                {t('failures.columns.reason')}
              </th>
            </tr>
          </thead>
          <tbody>
            {failures.map((failure) => (
              <tr key={failure.rowNumber} className="tw:odd:bg-white tw:even:bg-gray-cool-50/40">
                <td className="tw:px-5 tw:py-2.5 tw:align-top tw:tabular-nums tw:text-gray-cool-600">
                  {failure.rowNumber}
                </td>
                <td className="tw:px-3 tw:py-2.5 tw:align-top tw:font-medium tw:text-gray-cool-900">
                  {failure.identifier}
                </td>
                <td className="tw:px-3 tw:py-2.5 tw:align-top">
                  <Badge color={failure.kind === ImportFailureKind.Failed ? 'error' : 'warning'} shape="rounded">
                    {t(`failures.${failure.kind}`)}
                  </Badge>
                </td>
                <td className="tw:px-3 tw:py-2.5 tw:align-top tw:text-gray-cool-700">
                  {tReason(failure.reason, failure.values)}
                  {failure.diagnostic && (
                    <span className="tw:mt-1 tw:block tw:font-mono tw:text-xs tw:text-gray-cool-600">
                      {t('failures.diagnostic', { detail: failure.diagnostic })}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="tw:mb-0 tw:border-t tw:border-gray-100 tw:px-5 tw:py-3 tw:text-xs tw:text-gray-cool-600">
        {t('failures.key', { failed: t('failures.failed'), skipped: t('failures.skipped') })}
      </p>
    </Card>
  )
}

function WarningsTable({ warnings }: { warnings: ImportWarning[] }) {
  return (
    <Card>
      <CardHeading
        icon="info-circle"
        title={t('warnings.title', { count: new Set(warnings.map((warning) => warning.rowNumber)).size })}
        subtitle={t('warnings.subtitle')}
      />
      <div className="tw:max-h-[24rem] tw:overflow-auto">
        <table className="tw:min-w-full tw:border-collapse tw:text-sm">
          <thead className="tw:sticky tw:top-0">
            <tr className="tw:bg-gray-cool-50 tw:text-left tw:text-xs tw:text-gray-cool-600">
              <th scope="col" className="tw:w-16 tw:border-b tw:border-gray-200 tw:px-5 tw:py-2 tw:font-medium">
                {t('failures.columns.row')}
              </th>
              <th scope="col" className="tw:border-b tw:border-gray-200 tw:px-3 tw:py-2 tw:font-medium">
                {t('failures.columns.identifier')}
              </th>
              <th scope="col" className="tw:border-b tw:border-gray-200 tw:px-3 tw:py-2 tw:font-medium">
                {t('failures.columns.reason')}
              </th>
            </tr>
          </thead>
          <tbody>
            {warnings.map((warning) => (
              <tr key={`${warning.rowNumber}-${warning.reason}`} className="tw:odd:bg-white tw:even:bg-gray-cool-50/40">
                <td className="tw:px-5 tw:py-2.5 tw:tabular-nums tw:text-gray-cool-600">{warning.rowNumber}</td>
                <td className="tw:px-3 tw:py-2.5 tw:font-medium tw:text-gray-cool-900">{warning.identifier}</td>
                <td className="tw:px-3 tw:py-2.5 tw:text-gray-cool-700">{tReason(warning.reason, warning.values)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  )
}

function FileCard({ import: item }: { import: Import }) {
  return (
    <Card>
      <CardHeading
        icon="file-05"
        title={t('file.rows_title')}
        subtitle={t('file.rows_subtitle')}
        actions={
          item.fileHref && (
            <Link href={item.fileHref} rank="secondary" size="sm" leadingIcon="download-cloud-02">
              {t('file.download')}
            </Link>
          )
        }
      />
      <dl className="tw:m-0 tw:grid tw:gap-3 tw:px-5 tw:py-4 tw:text-sm tw:sm:grid-cols-3">
        <div>
          <dt className="tw:text-xs tw:font-normal tw:text-gray-cool-600">{t('file.name')}</dt>
          <dd className="tw:m-0 tw:font-medium tw:text-gray-cool-900">{item.fileName ?? t('unnamed_file')}</dd>
        </div>
        {item.submittedRowCount !== null && (
          <div>
            <dt className="tw:text-xs tw:font-normal tw:text-gray-cool-600">{t('file.rows')}</dt>
            <dd className="tw:m-0 tw:font-medium tw:tabular-nums tw:text-gray-cool-900">
              {item.submittedRowCount.toLocaleString()}
            </dd>
          </div>
        )}
        {item.importer && (
          <div>
            <dt className="tw:text-xs tw:font-normal tw:text-gray-cool-600">{t('file.imported_by')}</dt>
            <dd className="tw:m-0 tw:font-medium tw:text-gray-cool-900">{item.importer}</dd>
          </div>
        )}
      </dl>
    </Card>
  )
}
